import * as React from 'react'
import styled from 'styled-components/macro'
import {
  InputWithLabel,
  MultiSelect,
  StyledLabel,
  Column,
  AlertModal,
  Spinner,
  InputDropdownOptional,
} from 'common/components'
import {SemanticInputType} from 'common/components/InputDropdownOptional'
import useCustomers, {CustomerType} from 'management/hooks/useCustomers'
import useDeviceStatuses from './hooks/useDeviceStatuses'
import useDevices from 'management/hooks/useDevices'
import usePostDeviceTransfer, {
  DeviceTransferItem,
} from './hooks/usePostDeviceTransfer'
import Swal from 'sweetalert2'
import {showToast} from 'common/components/Toastr'
import ResizableTable from 'common/tables/ResizableTable/ResizableTable'
import {ErrorText} from 'maintenance/plans/components/intervalStyles'
import {
  reducer,
  errorCheck,
  onChangeInput,
  columns,
  returnSelectedCustomer,
  DeviceTransferStateType,
} from './deviceTransferFormHelpers'
import {EditDeviceType} from './hooks/usePutDevice'
import Modal from 'common/Modal'
import Autocomplete from 'common/components/Autocomplete'

const StyledModal = styled(Modal)`
  h2 {
    width: 100%;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 32px;
  gap: 5px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 16px;
  justify-content: flex-start;
  > div {
    width: 100%;
    white-space: nowrap;
  }
`

const StyledMultiSelect = styled(MultiSelect<EditDeviceType>)`
  max-width: 600px;
`

const DeviceTransferForm = ({
  selected,
  dismissModal,
}: {
  selected: CustomerType
  dismissModal: () => void
}) => {
  const initialState: DeviceTransferStateType = {
    deviceTransfer: {
      FromidCustomer: selected?.idcustomer,
      ToidCustomer: undefined,
      Order_Num: '',
      deviceTransfers: [],
      idDevice_Status: 1,
    },
    selectedDeviceIDs: [],
    errors: {},
    unitPrice: null,
    showConfirmDialog: false,
  }
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const {data: availableDevices, status: availableDevicesStatus} = useDevices(
    state.deviceTransfer?.FromidCustomer,
  )
  const {data: customers, status: customersStatus} = useCustomers()
  const {data: deviceStatuses} = useDeviceStatuses()
  const {mutateAsync: postDeviceTransfer, status: postDeviceStatus} =
    usePostDeviceTransfer()

  const customerOptions = React.useMemo(() => {
    if (customers) {
      return customers?.map((customer: CustomerType) => ({
        name: customer.Customer_Name || '',
        value: customer.idcustomer as number,
        id: customer.idcustomer as number,
      }))
    } else return []
  }, [customers])

  const saveClicked = () => {
    const errors = errorCheck(state)
    dispatch({type: 'setState', data: {errors}})
    const errorCount = Object.keys(errors).length
    if (errorCount > 0) {
      Swal.fire({
        icon: 'error',
        title: 'We need a little more detail...',
        text: 'Please check that all required information is included and try again.',
      })
      return
    } else {
      dispatch({type: 'setState', data: {showConfirmDialog: true}})
    }
  }

  const deviceStatusOptions = React.useMemo(() => {
    if (deviceStatuses) {
      return deviceStatuses?.map(
        (deviceStatus: {
          idDevice_Status: number
          Status_Description: string
        }) => ({
          text: deviceStatus.Status_Description,
          value: deviceStatus.idDevice_Status,
          key: deviceStatus.idDevice_Status,
        }),
      )
    }
  }, [deviceStatuses])

  const submitForm = async () => {
    try {
      await postDeviceTransfer(state.deviceTransfer)
      showToast('Device successfully transferred', 'success')
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Something went wrong! Please check your entries and try again.',
      })
    }

    dismissModal()
  }

  const inputChanged = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChangeInput({e, dispatch, state})
  }

  const deviceSelected = (deviceIDs: (string | number)[]) => {
    const devices: DeviceTransferItem[] = []
    availableDevices?.forEach(device => {
      if (deviceIDs.includes(device.idDevice)) {
        devices.push({
          Serial_Number_Displayed: device.Serial_Number_Displayed,
          idDevice: device.idDevice,
          Unit_Price: state.unitPrice || 0,
        })
      }
    })
    const errors = errorCheck(state, 'selectedDeviceIDs', deviceIDs as number[])
    dispatch({
      type: 'setState',
      data: {
        selectedDeviceIDs: deviceIDs as number[],
        deviceTransfer: {...state.deviceTransfer, deviceTransfers: devices},
        errors,
      },
    })
  }

  const toIDCustomerChanged = ({value}: {value: SemanticInputType}) => {
    const errors = errorCheck(state, 'ToidCustomer', value as string | number)

    dispatch({
      type: 'updateDeviceTransfer',
      data: {
        values: {ToidCustomer: value as number},
        errors,
      },
    })
  }

  const statusChanged = ({value}: {value: SemanticInputType}) => {
    dispatch({
      type: 'setState',
      data: {
        deviceTransfer: {
          ...state.deviceTransfer,
          idDevice_Status: value as number,
        },
      },
    })
  }

  return (
    <>
      <StyledModal
        title={`${selected?.Customer_Name} - Device Transfer`}
        onDismiss={dismissModal}
        onSave={saveClicked}
        showDialog={true}
        showClose={true}
        width="90%"
        maxWidth="1200px"
        renderFooter={true}
        submitButtonText={'Save'}
        scrollable={false}
        submitting={postDeviceStatus === 'loading'}
      >
        <Container>
          <Row>
            <InputWithLabel
              label="Order Number"
              name="Order_Num"
              value={state.deviceTransfer.Order_Num || ''}
              onChange={inputChanged}
              required={true}
              errors={state.errors}
            />
            <Autocomplete
              label="From Customer"
              selectedID={state.deviceTransfer.FromidCustomer || ''}
              onChange={() => null}
              list={customerOptions || []}
              required={true}
              placeholder={'--Select Customer--'}
              loading={customersStatus === 'loading'}
              disabled={true}
            />
            <Autocomplete
              label="To Customer"
              selectedID={state.deviceTransfer.ToidCustomer || ''}
              onChange={toIDCustomerChanged}
              list={customerOptions || []}
              required={true}
              placeholder={'-- Select Customer --'}
              loading={customersStatus === 'loading'}
              disabled={false}
            />
            <InputWithLabel
              label="Unit Price"
              name="unitPrice"
              value={state.unitPrice === null ? '' : state.unitPrice}
              onChange={inputChanged}
              required={true}
              errors={state.errors}
              type="number"
              decimals={2}
            />
          </Row>
          <Row>
            {availableDevices && availableDevices.length > 0 ? (
              <Column>
                <Row>
                  <StyledLabel>{`Devices To Transfer (${
                    state.selectedDeviceIDs?.length || 0
                  })`}</StyledLabel>
                  {state.errors.deviceTransfers && (
                    <ErrorText>
                      {state.errors.deviceTransfers.message}
                    </ErrorText>
                  )}
                </Row>
                <StyledMultiSelect
                  list={availableDevices}
                  selectedIDs={state.selectedDeviceIDs}
                  idField="idDevice"
                  labelField={['Serial_Number_Displayed', 'Device_Type']}
                  customLabel="Serial_Number_Displayed -- (Device_Type)"
                  setChecked={deviceSelected}
                  typeLabel="Devices"
                />
              </Column>
            ) : availableDevicesStatus === 'loading' ? (
              <Spinner type="partial" />
            ) : (
              <></>
            )}
            <InputDropdownOptional
              label="Device Status"
              options={deviceStatusOptions || []}
              onChange={statusChanged}
              value={state.deviceTransfer.idDevice_Status}
              name="idDevice_Status"
              errors={state.errors}
              clearable={false}
            />
          </Row>
          <Row>
            <ResizableTable
              data={state.deviceTransfer.deviceTransfers}
              columns={columns}
              status="success"
              height="calc(-490px + 100vh)"
              showHeader={false}
              showSubHeader={false}
            />
          </Row>
        </Container>
      </StyledModal>
      <AlertModal
        showDialog={state.showConfirmDialog}
        onSave={() => {
          dispatch({type: 'setState', data: {showConfirmDialog: false}})
          submitForm()
        }}
        onDismiss={() =>
          dispatch({type: 'setState', data: {showConfirmDialog: false}})
        }
        showDenyButton={true}
        title={`Confirm Device Transfer`}
        text={`Transfer ${
          state.selectedDeviceIDs?.length || 0
        } device(s) to ${returnSelectedCustomer(
          customers,
          state.deviceTransfer.ToidCustomer,
        )}? \r\n \r\n This will also remove the assets from ${returnSelectedCustomer(
          customers,
          state.deviceTransfer.FromidCustomer,
        )} for the selected devices. This action cannot be undone.`}
        type={'info'}
      />
    </>
  )
}
export default DeviceTransferForm
